import * as React from "react";
import { Upload, Button, Table, Alert, Spin, Breadcrumb } from "antd";
import { UploadOutlined, ImportOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { FetchWithAuth } from "digimaker-ui/util";
import {
  ParsedRow,
  ImportResult,
  ImportState,
  emptyImportState,
  parseExcel,
  buildTableColumns,
} from "./importUtils";

const HEADER_MAP: Record<string, string> = {
  "Student No.": "student_no",
  "First Name": "first_name",
  Surname: "surname",
  Nationality: "nationality",
  Sex: "sex",
  DOB: "dob",
  "Tel/Mobile": "telephone",
  Email: "email",
  "Address 1": "address1",
  "Address 2": "address2",
  "Address 3": "address3",
  "Address 4": "address4",
  Course: "course",
  Duration: "duration",
  Stage: "stage",
  "Starting Date": "starting_date",
};

const EXAMPLE_ROW: ParsedRow = {
  student_no: "111203",
  first_name: "Thais",
  surname: "Bonatti",
  nationality: "Italian",
  sex: "F",
  dob: "1994-10-23",
  telephone: "838370813",
  email: "thais@example.com",
  address1: "Flat 6, 7 Saint Peters Road",
  address2: "Dublin 7",
  address3: "Dublin",
  address4: "Dublin",
  course: "BA (Honours) in Accounting and Finance",
  duration: "3",
  stage: "",
  starting_date: "Sep-22",
};

const columns = buildTableColumns(HEADER_MAP);

const ImportStudents: React.FC = () => {
  const [state, setState] = React.useState<ImportState>(emptyImportState());

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result as ArrayBuffer;
      const rows = parseExcel(data, HEADER_MAP);
      setState((s) => ({
        ...s,
        rows,
        fileName: file.name,
        result: null,
        error:
          rows.length === 0
            ? "No valid rows found. Check the Excel format."
            : "",
      }));
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleImport = () => {
    if (state.rows.length === 0) return;
    setState((s) => ({ ...s, loading: true, result: null, error: "" }));
    FetchWithAuth("icd/import/students", {
      method: "POST",
      body: JSON.stringify(state.rows),
    })
      .then((data: any) =>
        setState((s) => ({
          ...s,
          loading: false,
          result: data.data as ImportResult,
        })),
      )
      .catch((err) =>
        setState((s) => ({ ...s, loading: false, error: String(err) })),
      );
  };

  return (
    <div>
      <h2>Import Students</h2>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/">Home</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Import Students</Breadcrumb.Item>
      </Breadcrumb>

      <p style={{ color: "#555", marginTop: 8, marginBottom: 4 }}>
        Example Excel format (first row is header):
      </p>
      <Table
        dataSource={[EXAMPLE_ROW]}
        columns={columns}
        rowKey={() => "example"}
        size="small"
        pagination={false}
        scroll={{ x: true }}
      />

      <div style={{ marginTop: 16 }}>
        <Upload
          accept=".xlsx,.xls"
          showUploadList={false}
          beforeUpload={handleFile}
        >
          <Button icon={<UploadOutlined />}>Choose Excel file</Button>
        </Upload>

        {state.fileName && (
          <span style={{ marginLeft: 12, color: "#666" }}>
            {state.fileName} — {state.rows.length} rows
          </span>
        )}

        {state.error && (
          <Alert type="error" message={state.error} style={{ marginTop: 12 }} />
        )}

        {state.rows.length > 0 && (
          <>
            <Table
              style={{ marginTop: 16 }}
              dataSource={state.rows}
              columns={columns}
              rowKey={(_, i) => String(i)}
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
            />
            <Button
              type="primary"
              icon={<ImportOutlined />}
              loading={state.loading}
              onClick={handleImport}
              style={{ marginTop: 8 }}
            >
              Import {state.rows.length} rows
            </Button>
          </>
        )}

        {state.loading && <Spin style={{ marginLeft: 16 }} />}

        {state.result && (
          <Alert
            style={{ marginTop: 16 }}
            type={state.result.errors.length > 0 ? "warning" : "success"}
            message={
              <div>
                Imported {state.result.imported} rows{" "}
                <Link to="/student/list">Back</Link>
              </div>
            }
            description={
              state.result.errors.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {state.result.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  );
};

export default ImportStudents;
